import { findAll, findFirst, prefersReducedMotion } from './dom.js';

export function parseSpecialtySlides(value) {
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    const slides = parsed.map((slide) => ({
      src: typeof slide?.src === 'string' ? slide.src.trim() : '',
      label: typeof slide?.label === 'string' ? slide.label.trim() : '',
    }));
    return slides.every(({ src, label }) => src && label) ? slides : [];
  } catch {
    return [];
  }
}

export function readAuthoredSlides(carousel) {
  const jsonSlides = parseSpecialtySlides(carousel.getAttribute('data-bp-specialty-slides'));
  if (jsonSlides.length > 0) return jsonSlides;

  const indexedSlides = [];
  for (let index = 0; index < 32; index += 1) {
    const src = carousel.getAttribute(`data-bp-slide-${index}-src`)?.trim() ?? '';
    const label = carousel.getAttribute(`data-bp-slide-${index}-label`)?.trim() ?? '';
    if (!src && !label) break;
    if (!src || !label) return [];
    indexedSlides.push({ src, label });
  }
  return indexedSlides;
}

export function advanceSpecialtyIndex(current, direction, length) {
  if (!Number.isInteger(length) || length < 1) return 0;
  return ((current + direction) % length + length) % length;
}

export function initializeSpecialtyCarousels(root, environment = {}) {
  const carousels = findAll(root, '[data-bp-specialty="carousel"][data-bp-specialty-runtime="v2"]');
  const windowObject = environment.window ?? globalThis;
  const delay = environment.setTimeout ?? windowObject.setTimeout?.bind(windowObject) ?? ((callback) => callback());
  const reducedMotion = prefersReducedMotion(windowObject);
  let initialized = 0;

  for (const carousel of carousels) {
    if (carousel.getAttribute('data-bp-specialty-ready') === 'true') continue;
    const slides = readAuthoredSlides(carousel);
    const image = findFirst(carousel, '.hn-spec-img');
    const label = findFirst(carousel, '.hn-spec-lbl');
    const strips = findAll(carousel, '[data-bp-strip]');
    const pointerControls = findAll(carousel, '[data-bp-spec]');
    const keyboardControls = findAll(carousel, '.hn-spec-btn[data-bp-spec]');
    if (slides.length === 0 || !image || pointerControls.length === 0) continue;

    let index = Number.parseInt(carousel.getAttribute('data-bp-specialty-index') ?? '0', 10);
    if (!Number.isInteger(index) || index < 0 || index >= slides.length) index = 0;
    let busy = false;

    const slideAt = (offset) => slides[advanceSpecialtyIndex(index, offset, slides.length)];
    const applyCurrentSlide = () => {
      const slide = slideAt(0);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = slide.src;
      image.alt = slide.label;
      if (label) label.textContent = slide.label;
      image.style.opacity = '1';
      carousel.setAttribute('data-bp-specialty-index', String(index));
      busy = false;
    };
    const render = (instant) => {
      for (const strip of strips) {
        const offset = Number.parseInt(strip.getAttribute('data-bp-strip') ?? '0', 10);
        if (Number.isInteger(offset)) strip.style.backgroundImage = `url("${slideAt(offset).src}")`;
      }
      if (instant || reducedMotion) {
        applyCurrentSlide();
        return;
      }
      busy = true;
      image.style.opacity = '0';
      delay(applyCurrentSlide, 220);
    };
    const move = (event, direction) => {
      event.preventDefault();
      if (busy) return;
      index = advanceSpecialtyIndex(index, direction, slides.length);
      render(false);
    };

    for (const control of pointerControls) {
      const direction = control.getAttribute('data-bp-spec') === 'next' ? 1 : -1;
      control.addEventListener('click', (event) => move(event, direction));
    }
    for (const control of keyboardControls) {
      const direction = control.getAttribute('data-bp-spec') === 'next' ? 1 : -1;
      control.addEventListener('keydown', (event) => {
        const usesNativeEnter = control.tagName === 'BUTTON'
          || (control.tagName === 'A' && Boolean(control.getAttribute('href')));
        if (event.key === ' ' || (event.key === 'Enter' && !usesNativeEnter)) move(event, direction);
      });
    }

    if (label) label.setAttribute('aria-live', 'polite');
    render(true);
    carousel.setAttribute('data-bp-specialty-ready', 'true');
    initialized += 1;
  }

  return initialized;
}
